import type { StorefrontPostSummary } from '@/features/storefront/api/storefront';
import type { StorefrontDataSource } from '@/features/storefront/data/source';
import { postArtwork } from '@/previewThemes/catalog/artwork';

/**
 * The newest posts across a shop's blogs, for the `blog_posts` section.
 *
 * The storefront API lists posts one blog at a time, and most shops have one
 * blog, so this asks for each blog's newest few and merges them rather than
 * adding an endpoint. A shop with a dozen blogs is read up to `MAX_BLOGS` deep:
 * a teaser on the home page is not an archive.
 */

/** The most posts one section shows. Mirrors `BlogPostsSection.limit`. */
export const TEASER_MAX_POSTS = 6;
const MAX_BLOGS = 5;

export interface TeaserPost extends StorefrontPostSummary {
  /** The blog the post is in, for its link. */
  blogHandle: string;
}

export interface BlogTeaser {
  /** Every blog's handle, in the shop's own order. */
  blogs: string[];
  /** Newest first, across every blog read. */
  posts: TeaserPost[];
}

export const EMPTY_TEASER: BlogTeaser = { blogs: [], posts: [] };

/**
 * The cover to draw for a post: its own picture, else - for a sample post a
 * theme seeded without one - the artwork the theme's preview drew it with, so
 * the live shop opens looking like the preview. `null` for a merchant's own
 * post without a cover, which keeps its plain frame.
 */
export function postCoverUrl(
  post: Pick<StorefrontPostSummary, 'handle' | 'image_url' | 'is_sample'>,
): string | null {
  if (post.image_url) return post.image_url;
  return post.is_sample ? postArtwork(post.handle) : null;
}

function newestFirst(a: TeaserPost, b: TeaserPost): number {
  const time = (post: TeaserPost) => (post.published_at ? Date.parse(post.published_at) : 0);
  return time(b) - time(a);
}

/** One query's worth: the blogs, and each one's newest posts. A blog that fails is skipped. */
export async function fetchBlogTeaser(source: StorefrontDataSource): Promise<BlogTeaser> {
  const blogs = (await source.blogs()).map((blog) => blog.handle);
  const pages = await Promise.all(
    blogs.slice(0, MAX_BLOGS).map(async (handle) => {
      try {
        const page = await source.posts(handle, { page: 1, size: TEASER_MAX_POSTS });
        return page.items.map((post) => ({ ...post, blogHandle: handle }));
      } catch {
        return [];
      }
    }),
  );
  return { blogs, posts: pages.flat().sort(newestFirst) };
}

/** What one section shows: its blog's posts (every blog's when blank), `limit` of them. */
export function teaserPosts(teaser: BlogTeaser, blog: string, limit: number): TeaserPost[] {
  const handle = blog.trim();
  const posts = handle ? teaser.posts.filter((post) => post.blogHandle === handle) : teaser.posts;
  return posts.slice(0, limit);
}

/** Where "Read the journal" goes: the section's blog, else the shop's first. */
export function teaserIndexPath(teaser: BlogTeaser, blog: string): string | null {
  const handle = blog.trim() || teaser.blogs[0];
  return handle ? `/blogs/${encodeURIComponent(handle)}` : null;
}
